import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Container,
  Paper,
  Divider,
  Alert,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Stack
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { installationApi, InstallationRequest } from '../../services/installationApi';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessInfo, hasBusinessInfo } from '../../config/environment';

// 동의 버전 상수
const TERMS_VERSION = '1.1';
const PRIVACY_VERSION = '1.1';

interface PendingApprovalProps {
  userStatus?: string;
  rejectionReason?: string;
}

const PendingApproval: React.FC<PendingApprovalProps> = ({ 
  userStatus,
  rejectionReason 
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentUser, logout } = useAuth();
  
  // props가 없으면 currentUser에서 가져오기
  const approvalStatus = userStatus || currentUser?.approvalStatus || currentUser?.status;
  const rejectionReasonText = rejectionReason || currentUser?.rejectionReason;

  const [installationRequest, setInstallationRequest] = useState<InstallationRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openTermsDialog, setOpenTermsDialog] = useState(false);
  const [openPrivacyDialog, setOpenPrivacyDialog] = useState(false);
  const [openRefundDialog, setOpenRefundDialog] = useState(false);

  // 약관 내용 추출을 위한 ref
  const termsContentRef = React.useRef<HTMLDivElement>(null);
  const privacyContentRef = React.useRef<HTMLDivElement>(null);

  // 모바일 상태바 색상 설정 (검정색)
  useEffect(() => {
    // theme-color meta 태그 설정
    let themeColorMeta = document.querySelector('meta[name="theme-color"]');
    if (!themeColorMeta) {
      themeColorMeta = document.createElement('meta');
      themeColorMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(themeColorMeta);
    }
    themeColorMeta.setAttribute('content', '#000000');
    
    // iOS Safari 상태바 스타일 설정
    let appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
    if (!appleStatusBarMeta) {
      appleStatusBarMeta = document.createElement('meta');
      appleStatusBarMeta.setAttribute('name', 'apple-mobile-web-app-status-bar-style');
      document.head.appendChild(appleStatusBarMeta);
    }
    appleStatusBarMeta.setAttribute('content', 'black-translucent');
  }, []);

  // 페이지 로드 시 세팅 예약 정보 가져오기
  useEffect(() => {
    const fetchInstallationRequest = async () => {
      if (!currentUser?.id) {
        // 승인 거절 화면에서는 로그인 필요 메시지 표시 안 함
        if (approvalStatus !== 'rejected') {
          setError('로그인이 필요합니다.');
        }
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await installationApi.getInstallationRequestByUserId(currentUser.id);
        if (response.data) {
          setInstallationRequest(response.data);
        }
      } catch (err: any) {
        console.error('세팅 예약 정보 조회 실패:', err);
        // 에러가 발생해도 페이지는 표시 (정보가 없을 수도 있음)
      } finally {
        setLoading(false);
      }
    };

    fetchInstallationRequest();
  }, [currentUser]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy년 MM월 dd일', { locale: ko });
    } catch (err) {
      return dateString;
    }
  };

  // 시간 포맷팅 함수
  const formatTime = (timeString: string) => {
    try {
      const [hours, minutes] = timeString.split(':');
      const hour = parseInt(hours, 10);
      const period = hour < 12 ? '오전' : '오후';
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      return `${period} ${displayHour}시 ${minutes}분`;
    } catch (err) {
      return timeString;
    }
  };

  return (
    <Box
      sx={{
        minHeight: { xs: '100dvh', md: '100vh' },
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        backgroundColor: '#000000',
        overflowY: 'auto',
        // 상태바 영역까지 배경색 확장
        paddingTop: { xs: 'env(safe-area-inset-top, 0px)', md: 0 },
        paddingBottom: { xs: 'env(safe-area-inset-bottom, 0px)', md: 0 }
      }}
    >
      {/* 배경 이미지 레이어 - 주방 이미지 */}
      <Box
        sx={{
          position: 'absolute',
          // 상태바 영역까지 배경 이미지 확장
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0.8) 100%),
            url('/main-background.png')
          `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          opacity: 0.6,
          zIndex: 0
        }}
      />

      {/* 어두운 오버레이 */}
      <Box
        sx={{
          position: 'absolute',
          // 상태바 영역까지 오버레이 확장
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.7) 100%)',
          zIndex: 1
        }}
      />

      {/* 헤더 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 30, // 배경 오버레이보다 훨씬 위에 표시
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: { xs: 4, sm: 8, md: 18 },
          py: { xs: 1.5, sm: 2, md: 3 },
          width: '100%',
          flexShrink: 0, // 헤더는 축소되지 않음
          // 모바일: 상태바 아래부터 시작
          paddingTop: { xs: 'calc(1.5rem + var(--header-top-offset))', sm: 'calc(2rem + var(--header-top-offset))', md: 3 },
          // 모바일에서만 상단바를 조금 아래로
          marginTop: { xs: 1.5, sm: 1.5, md: 0 }
        }}
      >
        {/* 로고 */}
        <Typography
          component="div"
          onClick={() => navigate('/')}
          sx={{
            fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.5rem' },
            fontWeight: 900,
            color: '#0B7A75',
            letterSpacing: '-0.02em',
            fontFamily: '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", sans-serif',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          JUDAM
        </Typography>

        {/* 우측 버튼들 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          {/* 로그아웃 버튼 */}
          <Button
            variant="contained"
            color="inherit"
            onClick={handleLogout}
            sx={{
              '&.MuiButton-root': {
                backgroundColor: '#0B7A75 !important',
                color: 'white !important'
              },
              '&.MuiButton-contained': {
                backgroundColor: '#0B7A75 !important',
                color: 'white !important'
              },
              '&.MuiButton-containedPrimary': {
                backgroundColor: '#0B7A75 !important',
                color: 'white !important',
                backgroundImage: 'none !important'
              },
              backgroundColor: '#0B7A75 !important',
              color: 'white !important',
              backgroundImage: 'none !important',
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '1rem', md: '1rem' },
              px: { xs: 1.8, sm: 2, md: 2 },
              py: { xs: 0.25, sm: 0.375, md: 0.5 },
              minHeight: 'auto',
              height: 'auto',
              lineHeight: { xs: 1.8, sm: 1.8, md: 1.8 },
              borderRadius: '16px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#09645F !important',
                backgroundImage: 'none !important',
                '&.MuiButton-contained': {
                  backgroundColor: '#09645F !important'
                },
                '&.MuiButton-containedPrimary': {
                  backgroundColor: '#09645F !important',
                  backgroundImage: 'none !important'
                }
              }
            }}
          >
            로그아웃
          </Button>
        </Box>
      </Box>

      {/* 메인 콘텐츠 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 2,
          paddingBottom: 0,
          width: '100%',
          maxWidth: '100%'
        }}
      >
        <Container
          maxWidth="sm"
          sx={{
            maxWidth: { xs: '100%', sm: '50%', md: '666px !important' },
            py: 4,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 4 },
                width: '100%',
                borderRadius: '16px',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: 'none'
              }}
            >
              {/* 로고 섹션 */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: '#1a2332',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    boxShadow: 'none',
                    overflow: 'hidden'
                  }}
                >
                  <img
                    src="/Icon.png"
                    alt="주담 로고"
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover'
                    }}
                  />
                </Box>
                <Typography
                  component="h1"
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    background: '#1a2332',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1
                  }}
                >
                  주방을 담다
                </Typography>
                <Typography
                  component="h2"
                  variant="h5"
                  align="center"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    background: '#1a2332',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  승인 대기
                </Typography>
              </Box>

              {error && approvalStatus !== 'rejected' && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                  {error}
                </Alert>
              )}

              {/* 승인 대기 또는 거절 메시지 */}
              {approvalStatus === 'rejected' ? (
                <Box sx={{ textAlign: 'center', mb: 3, width: '100%', minWidth: 0 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'error.main' }}>
                    가입신청이 거절되었습니다
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}>
                    담당자가 연락드릴 예정입니다.
                  </Typography>
                  {rejectionReasonText && (
                    <Paper
                      variant="outlined"
                      sx={{
                        mt: 2,
                        p: 2,
                        backgroundColor: '#fff3cd',
                        borderColor: 'error.main',
                        maxWidth: '100%',
                        minWidth: 0,
                        overflow: 'hidden'
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                        거절 사유
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          overflow: 'hidden'
                        }}
                      >
                        {rejectionReasonText}
                      </Typography>
                    </Paper>
                  )}
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    관리자 승인 대기 중입니다
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.7rem', sm: '0.8rem' }
                  }}>
                    승인 완료 후 서비스 이용이 가능합니다.
                  </Typography>
                </Box>
              )}

              {/* 세팅 예약 일정 정보 박스 */}
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}>
                    세팅 예약 정보를 불러오는 중...
                  </Typography>
                </Box>
              ) : installationRequest ? (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    세팅 예약 일정
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack spacing={2}>
                    {installationRequest.scheduledDate && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CalendarIcon sx={{ color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            예약 날짜
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDate(installationRequest.scheduledDate)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {installationRequest.scheduledTime && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <TimeIcon sx={{ color: 'primary.main' }} />
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            예약 시간
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatTime(installationRequest.scheduledTime)}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {installationRequest.specialRequests && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          특별 요청사항
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {installationRequest.specialRequests}
                        </Typography>
                      </Box>
                    )}
                    {installationRequest.assignedTo && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          담당자
                        </Typography>
                        <Typography variant="body2">
                          {installationRequest.assignedTo}
                          {installationRequest.assignedToPhone && ` (${installationRequest.assignedToPhone})`}
                        </Typography>
                      </Box>
                    )}
                    {installationRequest.notes && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                          안내 메시지
                        </Typography>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {installationRequest.notes}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              ) : (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    mb: 3,
                    backgroundColor: '#fafafa'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    textAlign: 'center',
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}>
                    세팅 예약 정보가 없습니다.
                  </Typography>
                </Paper>
              )}

              {/* 버튼 */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  onClick={handleLogout}
                  sx={{
                    minWidth: 120,
                    background: '#1a2332',
                    backgroundImage: 'none'
                  }}
                >
                  로그인 페이지로 돌아가기
                </Button>
              </Box>

              {/* 이용약관 다이얼로그 */}
              <Dialog
                open={openTermsDialog}
                onClose={() => setOpenTermsDialog(false)}
                maxWidth="md"
                fullWidth
                keepMounted={true}
                PaperProps={{
                  sx: {
                    maxHeight: '80vh'
                  }
                }}
              >
                <DialogTitle>이용약관</DialogTitle>
                <DialogContent dividers>
                  <div ref={termsContentRef}>
                    <Typography variant="h6" gutterBottom>
                      제1조 (목적)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      본 약관은 주담(이하 "회사")이 제공하는 주문 통합 관리 서비스(이하 "서비스")의 이용과 관련하여 회사와 이용자 간의 권리, 의무 및 책임사항을 규정함을 목적으로 합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제2조 (정의)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. "서비스"란 회사가 제공하는 주문 통합 관리, 재고 관리, 메뉴 관리 등의 서비스를 의미합니다.
                      <br />
                      2. "이용자"란 본 약관에 동의하고 회사가 제공하는 서비스를 이용하는 자를 의미합니다.
                      <br />
                      3. "브릿지앱"이란 이용자의 포스기에 설치되어 주문 데이터를 수집하는 애플리케이션을 의미합니다.
                      <br />
                      4. "이용자 데이터"란 이용자가 서비스 이용 과정에서 입력, 생성, 전송, 저장하는 모든 정보(주문 내역, 재고/메뉴 정보, 매장 정보, 이용 기록 등)를 의미합니다.
                      <br />
                      5. "개인정보"란 관련 법령에서 정한 바에 따라 개인을 식별할 수 있는 정보 또는 다른 정보와 쉽게 결합하여 개인을 식별할 수 있는 정보를 의미합니다.
                      <br />
                      6. "가명정보"란 개인정보의 일부를 삭제하거나 일부 또는 전부를 대체하는 등의 방법으로 추가 정보가 없이는 특정 개인을 알아볼 수 없도록 처리한 정보를 의미합니다.
                      <br />
                      7. "익명정보"란 시간·비용·기술 등을 합리적으로 고려할 때 다른 정보를 사용하여도 더 이상 개인을 알아볼 수 없는 정보를 의미합니다.
                      <br />
                      8. "추가정보"란 가명정보를 원래 상태로 복원하거나 특정 개인을 알아보기 위하여 사용될 수 있는 정보를 의미합니다.
                      <br />
                      9. "회원"이란 본 약관에 동의하고 회사와 이용계약을 체결하여 서비스 이용 자격을 부여받은 자를 말합니다.
                      <br />
                      10. "구독"이란 회원이 서비스 이용을 위해 월 단위 또는 연 단위로 이용요금을 지불하고 서비스 이용 권한을 부여받는 과금 형태를 말합니다.
                      <br />
                      11. "결제수단"이란 신용카드 등 회사가 정한 방법으로 이용요금을 결제할 수 있는 수단을 말합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제3조 (주문 데이터 수집 및 처리)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 이용자는 본 서비스 이용을 위해 포스기에 브릿지앱을 설치하고, 각 주문 접수 플랫폼(배달의민족, 요기요, 쿠팡이츠 등) 및 포스기로부터 발생하는 주문 내역을 브릿지앱을 통해 수집하는 것에 동의합니다.
                      <br />
                      2. 이용자는 브릿지앱을 통한 주문 내역 수집이 본인의 동의 및 요청에 따른 것임을 확인합니다.
                      <br />
                      3. 회사는 이용자가 동의한 범위 내에서만 주문 데이터를 수집 및 처리합니다.
                      <br />
                      4. 회사는 서비스 제공을 위하여 필요한 범위에서 주문 데이터의 저장, 조회, 분석, 표시, 전송 등의 처리를 수행할 수 있습니다.
                      <br />
                      5. 회사는 서비스 제공 및 품질 개선을 위하여 주문 데이터, 이용 기록(로그)을 처리할 수 있으며, 관련 법령 및 개인정보처리방침에 따릅니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제4조 (책임의 제한)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 각 주문 접수 플랫폼 및 포스기로부터 주문 내역을 수집하는 행위에 대해, 해당 플랫폼 및 포스기 제공업체와의 계약 관계, 기술적 문제, 데이터 전송 오류 등으로 인한 책임을 지지 않습니다.
                      <br />
                      2. 이용자는 브릿지앱 설치 및 운영에 대한 모든 책임을 부담하며, 각 플랫폼 및 포스기 제공업체와의 계약 및 이용약관을 준수할 의무가 있습니다.
                      <br />
                      3. 회사는 이용자의 포스기 환경, 네트워크 상태, 각 플랫폼의 정책 변경 등으로 인한 주문 데이터 수집 실패에 대해 책임을 지지 않습니다.
                      <br />
                      4. 회사의 고의 또는 중대한 과실로 인한 경우를 제외하고, 회사는 이용자와 주문 플랫폼/포스기 제공업체 간의 계약관계, 정책 변경, 서비스 장애 등 외부 요인으로 인해 발생한 손해에 대하여 책임을 지지 않습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제5조 (서비스의 제공 및 변경)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 이용자에게 주문 통합 관리, 재고 관리, 메뉴 관리 등의 서비스를 제공합니다.
                      <br />
                      2. 회사는 서비스의 내용을 변경할 수 있으며, 변경 시 이용자에게 사전 통지합니다.
                      <br />
                      3. 회사는 서비스 품질 개선, 보안 강화, 기능 추가/삭제, 정책 변경 등을 위하여 일부 기능을 시험 운영(베타)할 수 있습니다. 베타 기능의 제공 여부 및 범위는 회사의 판단에 따릅니다.
                      <br />
                      4. 회사는 서비스의 전부 또는 일부를 운영상/기술상 필요에 따라 변경 또는 중단할 수 있으며, 중대한 변경 또는 중단 시 사전 고지합니다(불가피한 경우 사후 고지 가능).
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제6조 (이용자의 의무)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 이용자는 본 약관 및 관련 법령을 준수해야 합니다.
                      <br />
                      2. 이용자는 서비스 이용과 관련하여 타인의 권리를 침해하거나 법령을 위반하는 행위를 하지 않아야 합니다.
                      <br />
                      3. 이용자는 본인의 계정 정보 및 접근수단을 적절히 관리해야 하며, 제3자에게 양도·대여·공유해서는 안 됩니다.
                      <br />
                      4. 이용자는 서비스의 정상적인 운영을 방해하는 행위(비정상적인 접근, 과도한 트래픽 유발, 해킹 시도, 리버스 엔지니어링 등)를 해서는 안 됩니다.
                      <br />
                      5. 이용자는 서비스 이용을 위해 필요한 권한(주문 플랫폼/포스기 계정 권한, 사업장 권한, 위임 권한 등)을 정당하게 보유해야 하며, 타인의 계정/권한을 무단 사용해서는 안 됩니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제7조 (서비스 이용 제한 및 이용계약 해지)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 다음 각 호의 사유가 있는 경우 사전 통지 후 서비스 이용을 제한(일시 정지, 일부 기능 제한 등)할 수 있습니다. 다만, 긴급한 경우 사후 통지할 수 있습니다.
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;가. 본 약관 또는 관련 법령 위반
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;나. 결제 미납 또는 부정 결제 의심
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;다. 비정상적 트래픽, 해킹 시도, 리버스 엔지니어링 등 서비스 운영 방해
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;라. 권한 없는 주문 데이터 수집 요구 또는 플랫폼 정책 위반 행위
                      <br />
                      2. 이용자가 시정 요구에도 불구하고 위반 상태를 해소하지 않거나 중대한 위반인 경우, 회사는 이용계약을 해지할 수 있습니다.
                      <br />
                      3. 이용자는 언제든지 서비스 내 기능 또는 고객지원 채널을 통해 이용계약 해지를 요청할 수 있습니다.
                      <br />
                      4. 이용계약이 해지되는 경우 서비스 이용 권한은 종료되며, 회사는 관련 법령 및 개인정보처리방침에 따라 이용자 데이터를 보관 또는 삭제합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제8조 (면책사항)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 천재지변, 전쟁, 기간통신사업자의 서비스 중지 등 불가항력으로 인한 서비스 중단에 대해 책임을 지지 않습니다.
                      <br />
                      2. 회사는 이용자의 귀책사유로 인한 서비스 이용 장애에 대해 책임을 지지 않습니다.
                      <br />
                      3. 회사는 이용자 데이터의 정확성, 완전성, 최신성에 대하여 보증하지 않으며, 이용자가 입력/연동한 정보에 기반한 의사결정 및 손익에 대하여 책임을 지지 않습니다.
                      <br />
                      4. 회사는 베타 기능 또는 시험 운영 기능에 대하여 상용 수준의 완전성, 안정성, 무결성을 보증하지 않습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제9조 (준거법 및 관할법원)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      본 약관은 대한민국 법률에 따라 해석되며, 서비스 이용과 관련하여 발생한 분쟁에 대해서는 회사의 본사 소재지를 관할하는 법원을 관할법원으로 합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제10조 (서비스 이용요금 및 수익모델)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 서비스 이용요금(월 구독/연 구독 등) 및 제공 범위를 서비스 화면, 공지 또는 별도 안내 페이지에 게시할 수 있습니다.
                      <br />
                      2. 회사는 프로모션/할인 이벤트를 운영할 수 있으며, 이벤트의 기간·대상·조건은 서비스 화면 또는 공지로 안내합니다.
                      <br />
                      3. 회사는 서비스 운영을 위하여 제휴 공급업체(식자재/도매/유통 등)로부터 수수료를 수취할 수 있으며, 이는 이용자의 서비스 이용요금과는 별개입니다.
                      <br />
                      4. 회사가 서비스 이용요금 또는 유료 기능을 신규 도입하거나 변경하는 경우, 관련 법령 및 회사 정책에 따라 사전에 이용자에게 고지합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제11조 (구독, 자동결제, 결제 실패 및 환불)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 구독 상품은 결제 시점에 즉시 서비스 이용 권한이 부여되며, 월 구독의 경우 결제일을 기준으로 매월 자동으로 갱신 결제될 수 있습니다.
                      <br />
                      2. 이용자는 다음 결제일 이전에 서비스 내 "구독 해지" 기능을 통해 자동결제를 해지할 수 있으며, 해지 시 다음 결제일부터 요금이 청구되지 않습니다(이미 결제된 이용기간은 원칙적으로 만료일까지 이용 가능).
                      <br />
                      3. 연 구독은 결제 즉시 1년 이용권이 부여되며, 중도 해지/환불은 관련 법령 및 회사의 환불 정책(서비스 내 별도 고지)에 따릅니다.
                      <br />
                      4. 결제 실패(한도 초과, 만료, 분실, 네트워크 오류 등)가 발생할 수 있으며, 회사는 서비스 제공을 위해 합리적인 범위에서 재결제를 시도하거나 이용자에게 결제수단 변경을 요청할 수 있습니다.
                      <br />
                      5. 결제 실패 상태가 일정 기간 지속되는 경우, 회사는 서비스 이용을 제한할 수 있습니다(예: 일부 기능 제한 또는 읽기 전용 전환).
                      <br />
                      6. 환불은 관련 법령 및 회사의 환불 정책에 따르며, 이용자가 이미 제공받은 서비스(사용 기간, 이용량 등)가 있는 경우 그 범위에서 공제될 수 있습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제12조 (제휴 공급업체 연동 및 주문·발주 처리)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 이용자가 서비스 내에서 공급업체 연동 기능(발주, 견적요청, 배송요청, 정산 등)을 이용하는 경우, 회사는 해당 기능 제공에 필요한 범위에서 이용자(매장) 정보 및 주문/발주 관련 정보를 해당 공급업체에게 제공하거나 전송할 수 있습니다.
                      <br />
                      2. 전항의 제공/전송에 관한 상세 항목, 제공받는 자, 목적, 보유·이용 기간 등은 회사의 개인정보 수집·이용 동의 및/또는 개인정보처리방침(해당하는 문서)에 따릅니다.
                      <br />
                      3. 이용자는 공급업체와의 거래(가격, 배송, 반품, 클레임, 세금계산서, 결제 조건 등)에 관하여 해당 공급업체의 정책 및 계약 조건을 확인해야 하며, 회사는 공급업체와 이용자 간 거래 내용 자체에 대한 책임을 지지 않습니다(단, 회사의 귀책사유로 인한 경우는 제외).
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제13조 (이용자 데이터의 이용 및 비식별 처리)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 서비스 제공, 품질 개선, 고객지원, 보안 및 부정이용 방지 목적을 위하여 이용자 데이터를 처리할 수 있습니다.
                      <br />
                      2. 회사는 통계작성, 과학적 연구 및 기술 개발, 서비스 고도화 등을 위하여 필요한 경우 관련 법령 및 회사 정책에 따라 이용자 데이터를 가명처리 또는 익명처리한 후 활용할 수 있습니다.
                      <br />
                      3. 회사는 가명정보/익명정보를 이용하여 특정 개인(대표자, 직원, 고객 등)을 재식별하려는 행위를 하지 않으며, 이를 금지합니다.
                      <br />
                      4. 회사는 가명정보 처리 시 추가정보를 가명정보와 분리하여 보관하고 접근권한을 최소화하는 등 안전조치를 적용합니다.
                      <br />
                      5. 이용자가 가명/익명 처리 후 데이터 활용에 관하여 별도의 선택 동의가 필요한 범위의 활용(예: 신규사업 모델 검증, 로봇/자동화 운영 연구 등)에 대하여는, 회사는 별도의 선택 동의를 받거나 관련 법령에 따른 절차를 준수합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제14조 (데이터 보관, 백업 및 삭제)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 이용자 데이터의 권리는 이용자에게 귀속됩니다.
                      <br />
                      2. 회사는 서비스 제공을 위해 필요한 범위에서 이용자 데이터를 저장·처리할 수 있습니다.
                      <br />
                      3. 이용자는 이용자 데이터의 보관 및 관리에 관하여 필요한 범위에서 자체적으로 백업할 책임이 있으며, 회사는 관련 법령에서 달리 정하는 경우를 제외하고 데이터의 영구 보관을 보장하지 않습니다.
                      <br />
                      4. 이용계약 종료 시 회사는 관련 법령 및 개인정보처리방침에 따라 이용자 데이터를 보관 또는 삭제할 수 있습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제15조 (지식재산권)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 서비스, 소프트웨어, 화면 구성, UI/UX, 로고, 상표, 문서, 콘텐츠 등에 대한 권리(저작권, 상표권 등 지식재산권)는 회사 또는 정당한 권리자에게 귀속됩니다.
                      <br />
                      2. 이용자는 서비스 이용을 통해 접근 가능한 회사의 지식재산권을 회사의 사전 서면 동의 없이 복제, 배포, 전송, 2차적 저작물 작성, 상업적 이용할 수 없습니다.
                      <br />
                      3. 이용자 데이터의 권리는 이용자에게 귀속되나, 이용자는 서비스 제공을 위해 필요한 범위에서 회사가 이용자 데이터를 처리하는 것에 동의합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제16조 (손해배상 및 책임 제한)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사의 고의 또는 중대한 과실로 인한 경우를 제외하고, 회사가 이용자에게 부담하는 손해배상 책임은 해당 손해 발생일 직전 3개월 동안 이용자가 회사에 실제로 지급한 이용요금 총액을 한도로 합니다(무료 이용 중인 경우에는 관련 법령이 허용하는 범위에서 책임을 제한합니다).
                      <br />
                      2. 회사는 간접손해, 특별손해, 결과손해, 영업손실, 데이터 손실 등 통상손해를 초과하는 손해에 대하여 책임을 지지 않습니다(단, 관련 법령에서 달리 정하는 경우는 제외).
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제17조 (약관의 개정)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 관련 법령을 위반하지 않는 범위에서 본 약관을 개정할 수 있습니다.
                      <br />
                      2. 회사가 약관을 개정할 경우 적용일자 및 개정사유를 명시하여 적용일자 7일 전부터 공지합니다. 다만, 이용자에게 불리한 개정의 경우에는 적용일자 30일 전부터 공지합니다.
                      <br />
                      3. 이용자가 개정 약관에 동의하지 않는 경우 서비스 이용을 중단하고 이용계약을 해지할 수 있습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제18조 (분쟁 해결 및 고객지원)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 이용자의 정당한 불만이나 의견을 반영하기 위해 고객지원 채널을 운영할 수 있습니다.
                      <br />
                      2. 서비스 이용과 관련하여 분쟁이 발생한 경우, 회사와 이용자는 성실히 협의하여 해결하도록 노력합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제19조 (주문 플랫폼 데이터 접근 권한 및 무단수집 금지)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 이용자는 각 주문 접수 플랫폼(배달의민족, 요기요, 쿠팡이츠 등) 및 포스기에서 발생하는 주문 내역에 대하여 정당한 접근 권한(계정 권한, 사업장 권한, 위임 권한 등)을 보유하고, 해당 플랫폼/포스기 제공업체의 이용약관 및 정책을 준수하여야 합니다.
                      <br />
                      2. 이용자는 회사가 브릿지앱을 통해 주문 내역을 수집·전송·처리하는 행위가 이용자의 요청 및 동의에 따른 것이며, 회사는 이용자가 부여한 권한 범위 내에서만 이를 수행한다는 점에 동의합니다.
                      <br />
                      3. 회사는 서비스 제공 과정에서 다음 각 호의 행위를 하지 않으며, 이용자 또한 회사에게 이를 요구해서는 안 됩니다.
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;1) 접근권한이 없는 정보의 취득 또는 제3자 정보의 무단 수집
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;2) 기술적 보호조치의 무력화·우회 또는 비정상적 방법으로 시스템에 접근하는 행위
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;3) 과도한 트래픽 유발 등 플랫폼/포스기 제공업체의 정상 운영을 방해하는 행위
                      <br />
                      4. 이용자가 제1항의 의무를 위반하거나, 이용자의 권한·약관 위반 등 이용자 귀책사유로 인하여 분쟁(플랫폼/포스기 제공업체의 이의제기, 계정 정지, 손해배상 청구, 행정·수사기관 조사 등)이 발생한 경우, 그에 대한 책임은 이용자에게 있으며, 회사는 관련 법령이 허용하는 범위에서 책임을 지지 않습니다(단, 회사의 고의 또는 중대한 과실로 인한 경우는 제외).
                      <br />
                      5. 각 주문 접수 플랫폼 또는 포스기 제공업체의 정책 변경, 연동 제한, 기술적 장애 등으로 인해 주문 내역 수집이 중단되거나 일부 실패할 수 있으며, 회사는 이를 해결하기 위해 합리적인 범위 내에서 지원할 수 있으나 결과를 보장하지 않습니다.
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary", fontSize: "0.875rem" }}>
                      시행일자: 2025년 12월 29일
                      <br />
                      약관 버전: {TERMS_VERSION}
                    </Typography>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenTermsDialog(false)}>닫기</Button>
                </DialogActions>
              </Dialog>

              {/* 개인정보처리방침 다이얼로그 */}
              <Dialog
                open={openPrivacyDialog}
                onClose={() => setOpenPrivacyDialog(false)}
                maxWidth="md"
                fullWidth
                keepMounted={true}
                PaperProps={{
                  sx: {
                    maxHeight: '80vh'
                  }
                }}
              >
                <DialogTitle>개인정보처리방침</DialogTitle>
                <DialogContent dividers>
                  <div ref={privacyContentRef}>
                    <Typography variant="h6" gutterBottom>
                      제1조 (개인정보의 처리 목적)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      회사는 다음의 목적을 위하여 개인정보를 처리합니다:
                      <br />
                      1. 서비스 제공: 주문 통합 관리, 재고 관리, 메뉴 관리 등 서비스 제공
                      <br />
                      2. 회원 관리: 회원 가입, 본인 확인, 회원 자격 유지·관리, 부정이용 방지
                      <br />
                      3. 주문 데이터 처리: 브릿지앱을 통한 주문 내역 수집 및 처리
                      <br />
                      4. 요금 결제 및 정기결제(구독) 처리, 결제내역 확인, 환불 처리(해당 시)
                      <br />
                      5. 고객지원: 문의/민원 처리, 공지 및 서비스 안내, 분쟁 대응
                      <br />
                      6. 서비스 품질 개선 및 보안: 서비스 이용 분석(통계), 장애 대응, 보안 및 부정이용 탐지
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제2조 (개인정보의 처리 및 보유 기간)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 법령에 따른 개인정보 보유·이용기간 또는 정보주체로부터 개인정보를 수집 시에 동의받은 개인정보 보유·이용기간 내에서 개인정보를 처리·보유합니다.
                      <br />
                      2. 각각의 개인정보 처리 및 보유 기간은 다음과 같습니다:
                      <br />
                      - 회원 가입 및 관리: 회원 탈퇴 시까지 (단, 관계 법령 위반에 따른 수사·조사 등이 진행중인 경우에는 해당 수사·조사 종료 시까지)
                      <br />
                      &nbsp;&nbsp;단, 부정이용 방지, 서비스 분쟁 대응, 고객문의 처리 및 이용자 보호를 위하여 다음의 최소 정보는 회원 탈퇴 후 1년간 별도로 분리 보관한 후 파기할 수 있습니다:
                      <br />
                      &nbsp;&nbsp;· 보관 항목(최소): 계정 식별정보(이메일 또는 아이디), 휴대전화번호(인증 여부 포함), 매장 식별정보(매장ID 등), 탈퇴일시, 부정이용/분쟁/문의 처리 이력(해당 시)
                      <br />
                      &nbsp;&nbsp;· 보관 사유: 부정이용 방지(재가입 악용, 비정상 이용 차단), 분쟁 해결 및 고객지원 이력 확인
                      <br />
                      &nbsp;&nbsp;· 보관 형태: 별도 분리 보관 및 접근권한 최소화
                      <br />
                      - 주문 데이터 및 서비스 이용기록(로그): 서비스 이용 기간 동안 또는 처리 목적 달성 시까지
                      <br />
                      - 결제 및 정기결제(구독) 관련 정보(해당 시): 거래 확인 및 정산/분쟁 대응을 위해 관련 법령에 따른 보관기간 또는 처리 목적 달성 시까지
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제3조 (개인정보의 제3자 제공)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      회사는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다. 다만, 다음의 경우에는 예외로 합니다:
                      <br />
                      1. 이용자가 사전에 동의한 경우
                      <br />
                      2. 법령의 규정에 의거하거나, 수사 목적으로 법령에 정해진 절차와 방법에 따라 수사기관의 요구가 있는 경우
                      <br />
                      3. 이용자가 서비스 내에서 공급업체 연동 기능(발주, 견적요청, 배송요청, 정산 등)을 이용하여 공급업체를 통한 재화/서비스 제공을 요청하는 경우, 원활한 거래 진행을 위하여 최소한의 개인정보 및 거래 관련 정보가 해당 공급업체에 제공될 수 있습니다.
                      <br />
                      &nbsp;&nbsp;- 제공받는 자: 서비스 내 연동된 공급업체(식자재/도매/유통 등)
                      <br />
                      &nbsp;&nbsp;- 제공 목적: 발주/배송/정산/클레임 처리 등 이용자가 요청한 거래 이행
                      <br />
                      &nbsp;&nbsp;- 제공 항목(예시): 매장명, 매장 주소, 담당자명, 담당자 연락처, 발주/주문 내역(품목, 수량, 희망납기 등), 배송요청 정보
                      <br />
                      &nbsp;&nbsp;- 보유·이용 기간: 거래 목적 달성 시까지 또는 관련 법령에 따른 보관기간
                      <br />
                      ※ 공급업체 목록 및 상세 제공 항목은 서비스 내 별도 페이지(또는 공지)로 최신 정보를 고지할 수 있습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제4조 (주문 데이터 수집 및 처리)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 이용자는 본 서비스 이용을 위해 포스기에 브릿지앱을 설치하고, 각 주문 접수 플랫폼(배달의민족, 요기요, 쿠팡이츠 등) 및 포스기로부터 발생하는 주문 내역을 브릿지앱을 통해 수집하는 것에 동의합니다.
                      <br />
                      2. 이용자는 브릿지앱을 통한 주문 내역 수집이 본인의 동의 및 요청에 따른 것임을 확인합니다.
                      <br />
                      3. 회사는 이용자가 동의한 범위 내에서만 주문 데이터를 수집 및 처리합니다.
                      <br />
                      4. 회사는 서비스 제공 및 장애 대응을 위해 필요한 범위에서 포스기/단말 식별정보, 접속기록 등 이용기록(로그)을 수집·이용할 수 있습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제5조 (개인정보의 파기)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 개인정보 보유기간의 경과, 처리목적 달성 등 개인정보가 불필요하게 되었을 때에는 지체 없이 해당 개인정보를 파기합니다.
                      <br />
                      2. 전자적 파일 형태의 개인정보는 복구 및 재생이 불가능한 방법으로 영구 삭제하며, 종이 문서에 출력된 개인정보는 분쇄하거나 소각하여 파기합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제6조 (정보주체의 권리·의무 및 행사방법)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      이용자는 언제든지 다음 각 호의 개인정보 보호 관련 권리를 행사할 수 있습니다:
                      <br />
                      1. 개인정보 열람요구
                      <br />
                      2. 오류 등이 있을 경우 정정 요구
                      <br />
                      3. 삭제요구
                      <br />
                      4. 처리정지 요구
                      <br />
                      5. 동의 철회 요구(동의 기반 처리에 한함)
                      <br />
                      ※ 권리 행사는 회사의 고객지원 채널을 통해 접수할 수 있으며, 법령에 따라 일부 권리가 제한될 수 있습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제7조 (처리하는 개인정보의 항목)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      회사는 제1조의 목적을 위하여 다음의 개인정보를 처리할 수 있습니다(필요 최소한 원칙).
                      <br />
                      1. 회원가입/계정 관리(필수): 담당자명, 휴대전화번호, 이메일, 비밀번호(또는 인증정보), 사업자 정보(상호/사업자등록번호 등, 해당 시), 매장명
                      <br />
                      2. 서비스 이용 및 고객지원(필수): 매장 주소(또는 소재지), 매장 연락처, 문의/상담 기록, 공지 수신 여부, 결제/환불 처리 관련 정보(해당 시)
                      <br />
                      3. 브릿지앱/주문 데이터 처리(필수): 주문 내역(주문번호, 주문시각, 주문채널, 품목/수량/금액 등 서비스 제공에 필요한 범위), 포스기/단말 식별정보(앱 구동 및 장애 대응에 필요한 범위), 서비스 이용기록(로그)
                      <br />
                      4. 공급업체 연동(해당 시): 발주/배송/정산에 필요한 정보(제3조 제3항 참조)
                      <br />
                      5. 마케팅/홍보(선택, 해당 시): 이메일, 휴대전화번호, 공지 수신 여부(이벤트/소식 등)
                      <br />
                      ※ 회사는 민감정보(건강정보 등) 및 고유식별정보(주민등록번호 등)를 원칙적으로 처리하지 않습니다. 처리하게 되는 경우 별도의 법적 근거 또는 별도 동의를 받습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제8조 (개인정보 처리 업무의 위탁)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 원활한 서비스 제공을 위해 개인정보 처리 업무의 일부를 외부에 위탁할 수 있습니다.
                      <br />
                      2. 회사는 위탁 계약 체결 시 관련 법령에 따라 수탁자가 개인정보를 안전하게 처리하도록 필요한 사항을 규정하고 관리·감독합니다.
                      <br />
                      3. 회사의 개인정보 처리 업무 위탁 현황은 다음과 같습니다(해당되는 경우에 한함):
                      <br />
                      - 수탁자: (주)알리는사람들(알리고) / 위탁업무: 문자(SMS/LMS), 카카오 알림톡 등 메시지 발송 및 발송 이력 관리
                      <br />
                      - 수탁자: (주)카카오 / 위탁업무: 카카오 알림톡 메시지 전달(카카오 비즈메시지 인프라)
                      <br />
                      - 수탁자: (주)카카오(daum) / 위탁업무: 이메일 발송/수신 및 고객지원 회신 처리(daum 메일 계정 사용 시)
                      <br />
                      - 수탁자: Google LLC / 위탁업무: 이메일 발송/수신 및 고객지원 회신 처리(Gmail 계정 사용 시)
                      <br />
                      - 수탁자: Google LLC / 위탁업무: 서비스 이용 분석(Google Analytics)
                      <br />
                      - 수탁자: 결제대행사/PG/정기결제 제공자(해당 시) / 위탁업무: 결제 처리, 정기결제(구독) 처리, 결제내역 확인, 환불 처리
                      <br />
                      ※ 회사가 실제로 이용하는 수탁자 및 위탁업무는 서비스 운영 현황에 따라 변경될 수 있으며, 변경 시 본 방침 또는 서비스 내 공지로 안내합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제9조 (개인정보의 국외 이전)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      회사는 서비스 제공 과정에서 다음과 같이 개인정보 처리 업무가 국외로 이전될 수 있습니다(해당 시).
                      <br />
                      1. 이전되는 항목: 웹/앱 이용정보(쿠키, 기기·브라우저 정보, 접속기록, 방문기록, 이벤트 로그 등), 서비스 이용기록(분석 목적 범위), 이메일 송수신/보관 정보(고객지원 목적 범위)
                      <br />
                      2. 이전 국가: 미국 등(수탁자의 서버 소재 국가)
                      <br />
                      3. 이전 시기 및 방법: 서비스 이용 시점에 네트워크를 통해 수시 전송
                      <br />
                      4. 이전받는 자: Google LLC
                      <br />
                      5. 이용 목적:
                      <br />
                      &nbsp;&nbsp;- Google Analytics: 서비스 이용 행태 분석 및 서비스 품질 개선, 통계 산출
                      <br />
                      &nbsp;&nbsp;- Gmail: 고객지원 문의 대응을 위한 이메일 송수신 및 보관
                      <br />
                      6. 보유·이용 기간:
                      <br />
                      &nbsp;&nbsp;- Google Analytics: 회사가 Google Analytics 보관 설정 및 서비스 이용 종료 시까지 또는 목적 달성 시까지
                      <br />
                      &nbsp;&nbsp;- Gmail: 문의 처리 목적 달성 시까지 또는 관련 법령에 따른 보관기간
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제10조 (가명/익명 처리 및 활용 안내)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 서비스 품질 개선, 통계작성, 과학적 연구 및 기술 개발 등을 위해 필요한 경우, 관련 법령에 따라 개인정보를 가명처리 또는 익명처리하여 활용할 수 있습니다.
                      <br />
                      2. 회사는 가명처리 시 추가정보를 가명정보와 분리 보관하고 접근권한을 최소화하는 등 안전조치를 적용합니다.
                      <br />
                      3. 회사 및 제휴사는 가명/익명 처리된 정보를 이용하여 특정 개인을 재식별하려는 행위를 금지합니다.
                      <br />
                      4. 가명/익명 처리 후 활용이 정보주체의 권리를 제한할 우려가 있거나, 서비스 기본 제공 목적을 넘어서는 범위로 확대되는 경우 회사는 별도의 선택 동의를 받습니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제11조 (주문 플랫폼 데이터 수집에 관한 확인)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      이용자는 브릿지앱을 통한 주문 내역 수집이 이용자의 동의 및 요청에 따른 것이며, 이용자가 해당 주문 접수 플랫폼 및 포스기에서 주문 내역에 접근·연동할 수 있는 정당한 권한을 보유하고 해당 플랫폼/포스기 제공업체의 약관 및 정책을 준수해야 함을 확인합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제12조 (개인정보의 안전성 확보조치)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      회사는 개인정보의 안전성 확보를 위해 다음과 같은 조치를 취할 수 있습니다.
                      <br />
                      1. 내부관리계획 수립 및 시행
                      <br />
                      2. 접근권한 관리 및 접근통제, 접속기록 보관 및 점검
                      <br />
                      3. 개인정보의 암호화(필요 시), 전송구간 보호
                      <br />
                      4. 보안프로그램 설치 및 점검, 취약점 대응
                      <br />
                      5. 가명정보 처리 시 추가정보 분리 보관 등 안전조치
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제13조 (개인정보 관련 문의처)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      - 카카오톡 채널: 주담
                      <br />
                      ※ 개인정보 관련 문의, 열람·정정·삭제·처리정지 요청은 위 문의처로 접수할 수 있습니다.
                      <br />
                      - 개인정보 보호책임자(또는 담당자): 이한상
                      <br />
                      - 연락처(이메일/전화): info@judam.kr
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제14조 (개인정보처리방침의 변경 및 고지)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      회사는 관련 법령 또는 서비스 변경에 따라 본 개인정보처리방침의 내용을 변경할 수 있으며, 변경 시 적용일자 및 주요 변경 내용을 사전에 고지합니다.
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary", fontSize: "0.875rem" }}>
                      시행일자: 2025년 12월 29일
                      <br />
                      개인정보처리방침 버전: {PRIVACY_VERSION}
                    </Typography>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenPrivacyDialog(false)}>닫기</Button>
                </DialogActions>
              </Dialog>

              {/* 환불정책 다이얼로그 */}
              <Dialog
                open={openRefundDialog}
                onClose={() => setOpenRefundDialog(false)}
                maxWidth="md"
                fullWidth
                keepMounted={true}
                PaperProps={{
                  sx: {
                    maxHeight: '80vh'
                  }
                }}
              >
                <DialogTitle>환불정책</DialogTitle>
                <DialogContent dividers>
                  <div>
                    <Typography variant="h6" gutterBottom>
                      제1조 (환불정책의 적용 범위)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      본 환불정책은 주담(이하 "회사")이 제공하는 주문 통합 관리 서비스(이하 "서비스")의 구독 상품(월 구독, 연 구독 등)에 대한 환불 및 해지에 관하여 적용됩니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제2조 (구독 해지 및 환불)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 월 구독 상품의 경우:
                      <br />
                      &nbsp;&nbsp;가. 이용자는 언제든지 서비스 내 "구독 해지" 기능을 통해 구독을 해지할 수 있습니다.
                      <br />
                      &nbsp;&nbsp;나. 구독 해지 시 다음 결제일부터 요금이 청구되지 않으며, 이미 결제된 이용기간은 원칙적으로 만료일까지 이용 가능합니다.
                      <br />
                      &nbsp;&nbsp;다. 월 구독 상품은 전자상거래법 제17조 제2항 제5호에 따라 디지털콘텐츠의 제공이 개시된 경우이므로, 이용 개시 후 환불은 원칙적으로 불가합니다. 다만, 다음 각 호의 경우에는 예외적으로 환불이 가능합니다:
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;1) 회사의 귀책사유로 인하여 서비스를 이용할 수 없는 경우
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;2) 서비스의 중대한 결함으로 인하여 정상적인 이용이 불가능한 경우
                      <br />
                      &nbsp;&nbsp;&nbsp;&nbsp;3) 기타 소비자기본법 등 관련 법령에 따라 환불이 필요한 경우
                      <br />
                      <br />
                      2. 연 구독 상품의 경우:
                      <br />
                      &nbsp;&nbsp;가. 연 구독은 결제 즉시 1년 이용권이 부여되며, 중도 해지 시 환불은 다음과 같이 처리됩니다:
                      <br />
                      &nbsp;&nbsp;나. 환불 금액 = 결제 금액 - (월 이용료 × 이용 개월 수) - 위약금(해당 시)
                      <br />
                      &nbsp;&nbsp;다. 위약금은 남은 이용기간의 10%를 초과하지 않으며, 최소 1개월 이용료를 차감합니다.
                      <br />
                      &nbsp;&nbsp;라. 환불은 환불 신청일로부터 영업일 기준 7일 이내에 처리됩니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제3조 (환불 절차)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 환불을 원하는 경우 회사의 고객지원 채널(카카오톡 채널: 주담)을 통해 환불 신청을 해야 합니다.
                      <br />
                      2. 환불 신청 시 다음 정보를 제공해야 합니다:
                      <br />
                      &nbsp;&nbsp;가. 회원 정보(이메일, 매장명)
                      <br />
                      &nbsp;&nbsp;나. 결제 정보(결제일, 결제금액, 결제수단)
                      <br />
                      &nbsp;&nbsp;다. 환불 사유
                      <br />
                      3. 회사는 환불 신청을 접수한 후 영업일 기준 7일 이내에 환불 여부를 결정하고, 환불이 승인된 경우 영업일 기준 7일 이내에 환불 처리를 완료합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제4조 (환불 불가 사유)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      다음 각 호의 경우에는 환불이 불가합니다:
                      <br />
                      1. 이용자가 본 약관 또는 관련 법령을 위반하여 서비스 이용이 제한된 경우
                      <br />
                      2. 이용자의 귀책사유로 인한 서비스 이용 중단
                      <br />
                      3. 무료 체험 기간 종료 후 정상적으로 결제된 구독 상품의 경우(단, 제2조 제2항의 예외 사유에 해당하는 경우는 제외)
                      <br />
                      4. 프로모션 또는 할인 이벤트를 통해 제공된 무료 이용권의 경우
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제5조 (환불 방법)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 환불은 원칙적으로 결제 시 사용한 결제수단으로 환불됩니다.
                      <br />
                      2. 신용카드 결제의 경우, 카드사 정책에 따라 환불까지 영업일 기준 3~5일이 소요될 수 있습니다.
                      <br />
                      3. 결제수단이 변경되거나 해지된 경우, 회사는 대체 환불 방법을 안내합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제6조 (부분 환불)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 연 구독 상품의 중도 해지 시, 이용자가 실제로 이용한 기간에 해당하는 금액을 차감한 나머지 금액이 환불됩니다.
                      <br />
                      2. 부분 환불 시 월 이용료는 일할 계산되지 않으며, 최소 1개월 단위로 계산됩니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제7조 (환불 정책의 변경)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      1. 회사는 관련 법령의 변경 또는 서비스 정책 변경에 따라 본 환불정책을 변경할 수 있습니다.
                      <br />
                      2. 환불정책 변경 시 변경 사항을 서비스 화면 또는 공지로 안내하며, 이용자에게 불리한 변경의 경우 적용일자 30일 전부터 공지합니다.
                    </Typography>

                    <Typography variant="h6" gutterBottom>
                      제8조 (환불 관련 문의)
                    </Typography>
                    <Typography variant="body2" paragraph>
                      환불 관련 문의사항은 다음 채널을 통해 접수할 수 있습니다:
                      <br />
                      - 카카오톡 채널: 주담
                      <br />
                      - 이메일: info@judam.kr
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 2, color: "text.secondary", fontSize: "0.875rem" }}>
                      시행일자: 2026년 1월 24일
                      <br />
                      환불정책 버전: 1.0
                    </Typography>
                  </div>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => setOpenRefundDialog(false)}>닫기</Button>
                </DialogActions>
              </Dialog>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* 푸터 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          flexShrink: 0,
          mt: 0,
          py: 6,
          px: { xs: 4, sm: 8, md: 18 },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          backdropFilter: 'blur(10px)',
          color: 'white'
        }}
      >
        <Container maxWidth="lg" sx={{ px: 0 }}>
          <Grid container spacing={4}>
            {/* 왼쪽: 브랜드 정보 */}
            <Grid item xs={12} md={4}>
              <Stack spacing={2}>
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.1rem', md: '1.1rem' },
                    fontWeight: 900,
                    color: '#0B7A75',
                    letterSpacing: '-0.02em',
                    fontFamily: '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, system-ui, "Segoe UI", "Apple SD Gothic Neo", "Noto Sans KR", sans-serif'
                  }}
                >
                  JUDAM
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.85rem', md: '1rem' },
                    color: 'rgba(255,255,255,0.7)',
                    lineHeight: 1.8
                  }}
                >
                  AI 기반 스마트 자동 재고관리 시스템
                  <br />
                  "주방을 담다"
                </Typography>
              </Stack>
            </Grid>

            {/* 오른쪽: 연락처 */}
            <Grid item xs={12} md={8}>
              <Stack spacing={2} sx={{ mt: { xs: 2, sm: 2, md: 0 } }}>
                <Typography
                  sx={{
                    fontSize: { xs: '0.85rem', sm: '0.85rem', md: '1.1rem' },
                    fontWeight: 700,
                    color: '#0B7A75'
                  }}
                >
                  문의하기
                </Typography>
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.8
                      }
                    }}
                    onClick={() => window.open('http://pf.kakao.com/_xdxhmLn/chat', '_blank')}
                  >
                    <Box
                      component="img"
                      src="/kakao.png"
                      alt="카카오톡"
                      sx={{
                        width: 24,
                        height: 24,
                        objectFit: 'contain',
                        borderRadius: '16px'
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: '0.85rem', sm: '0.85rem', md: '0.95rem' },
                        color: 'rgba(255,255,255,0.8)'
                      }}
                    >
                      카카오톡 채널: 주담
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Grid>
          </Grid>

          <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* 하단 정보 */}
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
            >
              {/* 사업자 정보 */}
              {hasBusinessInfo() && (
                <Typography
                  sx={{
                    fontSize: { xs: '0.5rem', sm: '0.85rem' },
                    color: 'rgba(255,255,255,0.5)',
                    textAlign: { xs: 'center', sm: 'left' },
                    lineHeight: 1.6
                  }}
                >
                  {(() => {
                    const businessInfo = getBusinessInfo();
                    return (
                      <>
                        상호명: {businessInfo.companyName} | 사업자등록번호: {businessInfo.businessRegistrationNumber} | 대표자: {businessInfo.ceoName}
                        <br />
                        주소: {businessInfo.businessAddress} | 전화번호: {businessInfo.phoneNumber}
                        {businessInfo.telecommunicationSalesNumber && (
                          <>
                            <br />
                            통신판매업번호: {businessInfo.telecommunicationSalesNumber}
                          </>
                        )}
                      </>
                    );
                  })()}
                </Typography>
              )}

              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Typography
                  onClick={() => setOpenTermsDialog(true)}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'white',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  이용약관
                </Typography>
                <Typography
                  onClick={() => setOpenPrivacyDialog(true)}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'white',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  개인정보처리방침
                </Typography>
                <Typography
                  onClick={() => setOpenRefundDialog(true)}
                  sx={{
                    fontSize: '0.85rem',
                    color: 'rgba(255,255,255,0.5)',
                    cursor: 'pointer',
                    '&:hover': {
                      color: 'white',
                      textDecoration: 'underline'
                    }
                  }}
                >
                  환불정책
                </Typography>
              </Stack>
            </Stack>

            <Typography
              sx={{
                fontSize: '0.85rem',
                color: 'rgba(255,255,255,0.4)',
                textAlign: 'center'
              }}
            >
              © 2026 주담 (JUDAM). All rights reserved.
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default PendingApproval;
